import { Injectable } from '@angular/core';
import {
  CreatePartnerDealRequest,
  UpdatePartnerDealAmountRequest,
} from '@interfaces/partners/requests/partner-deal.request';
import { LinkPartnerCompanyRequest } from '@interfaces/partners/requests/partner-company.request';
import {
  CreatePartnerRequest,
  UpdatePartnerRequest,
} from '@interfaces/partners/requests/partner.request';
import { PartnerDealTimelineResponse } from '@interfaces/partners/responses/partner-deal-timeline.response';
import { PartnerDealQuotedLoanResponse } from '@interfaces/partners/responses/partner-deal-quoted-loan.response';
import {
  PartnerEarningsResponse,
  PartnerFlatFeeSimulationResponse,
} from '@interfaces/partners/responses/partner-flat-fee.response';
import {
  PartnerBankJourneyCommentResponse,
  PartnerBankJourneyResponse,
} from '@interfaces/partners/responses/partner-bank-journey.response';
import {
  PartnerDealGatesProcessingResponse,
  PartnerDealGatesResponse,
} from '@interfaces/partners/responses/partner-deal-gates.response';
import {
  CommercialClientTermResponse,
  CommercialClientTermDocumentResponse,
  CommercialClientTermPreviewResponse,
  CommercialClientTermSuggestedSignatoryResponse,
  LegatusTermSignatoryResponse,
  TermSignerVerificationResponse,
} from '@interfaces/partners/responses/commercial-client-term.response';
import { AddCommercialClientTermSignatoryRequest } from '@interfaces/partners/requests/commercial-client-term.request';
import { ValidationResultResponse } from '@interfaces/partners/responses/validation-result.response';
import { unwrapValidation } from '@services/http/partners/unwrap-validation.operator';
import {
  RejectPartnerDealRequest,
  RevertPartnerDealRejectionRequest,
} from '@interfaces/partners/requests/partner-deal-decision.request';
import {
  PartnerCnpjLookupResponse,
  PartnerDealDocumentResponse,
  PartnerDealResponse,
  PartnerDealStage,
} from '@interfaces/partners/responses/partner-deal.response';
import {
  PartnerCompanyOptionResponse,
  PartnerCompanyResponse,
} from '@interfaces/partners/responses/partner-company.response';
import {
  PartnerPageResponse,
  PartnerResponse,
} from '@interfaces/partners/responses/partner.response';
import { PartnerUserResponse } from '@interfaces/partners/responses/partner-user.response';
import {
  CreatePartnerUserRequest,
  UpdatePartnerUserRequest,
} from '@interfaces/partners/requests/partner-user.request';
import {
  PartnerDealBriefingChatContextResponse,
  PartnerDealBriefingSummaryResponse,
  PartnerDealBriefingVersionIdResponse,
  PartnerDealInvestigationResponse,
} from '@interfaces/partners/responses/partner-deal-briefing.response';
import { GeneratePartnerDealBriefingRequest } from '@interfaces/partners/requests/partner-deal-briefing.request';
import { HttpEventType, HttpResponse } from '@angular/common/http';
import { VaultApiService } from '@services/api/vault-api.service';
import { Observable, filter, from, map, of, switchMap } from 'rxjs';

/**
 * Endpoints do controller de parceiros: o módulo Comercial do parceiro (cards 2902 e 2903)
 * e a administração dos parceiros pela Legatus (card 2916).
 *
 * O auto-cadastro (`sign-up`, `verify-code`) mora na landing page, que é outro projeto e
 * chama a API anonimamente — por isso não aparece aqui.
 */
@Injectable({
  providedIn: 'root',
})
export class PartnersHttpService {
  private readonly BASE_URL = 'partners';

  constructor(private readonly apiService: VaultApiService) {}

  /**
   * Consulta um CNPJ e diz se o parceiro logado pode registrar um deal para ele.
   *
   * @param cnpj CNPJ com ou sem máscara — o backend normaliza.
   */
  lookupCnpj(cnpj: string): Observable<PartnerCnpjLookupResponse | null> {
    return this.apiService.get<PartnerCnpjLookupResponse>(`${this.BASE_URL}/cnpj/${cnpj}`);
  }

  /** Registra o deal: cria a empresa, reserva o CNPJ e grava o deal, tudo numa transação. */
  createDeal(payload: CreatePartnerDealRequest): Observable<PartnerDealResponse | null> {
    return this.apiService.post<PartnerDealResponse>(`${this.BASE_URL}/deals`, payload);
  }

  /** Deals do parceiro logado, do mais recente para o mais antigo. */
  getMyDeals(): Observable<PartnerDealResponse[] | null> {
    return this.apiService.get<PartnerDealResponse[]>(`${this.BASE_URL}/deals`);
  }

  /**
   * Simula valor × flat fee do prazo × fee da Calculadora (card 3707). A conta é do backend; a Calculadora e o
   * Registrar deal só mostram o resultado.
   */
  simulateFlatFee(
    amount: number,
    termYears: number
  ): Observable<PartnerFlatFeeSimulationResponse | null> {
    return this.apiService.get<PartnerFlatFeeSimulationResponse>(
      `${this.BASE_URL}/calculator/simulate`,
      undefined,
      { amount, termYears }
    );
  }

  /** Volume em captação e fee estimado do parceiro logado, por moeda (Início do parceiro, card 3707). */
  getMyEarnings(): Observable<PartnerEarningsResponse | null> {
    return this.apiService.get<PartnerEarningsResponse>(`${this.BASE_URL}/me/earnings`);
  }

  /**
   * A linha do tempo do deal: por onde passou e quanto tempo ficou em cada etapa.
   *
   * ⚠️ Os tempos vêm **calculados do servidor**. A regra de "etapa visitada duas vezes soma as
   * passagens" mora lá de propósito, para que o board e a tela do deal nunca divirjam — e porque
   * calculada como intervalo único ela dá um número maior e perfeitamente plausível.
   *
   * O parceiro dono enxerga: é a aba que explica a ele por que o deal está parado.
   */
  getDealTimeline(dealId: string): Observable<PartnerDealTimelineResponse | null> {
    return this.apiService
      .get<
        ValidationResultResponse<PartnerDealTimelineResponse>
      >(`${this.BASE_URL}/deals/${dealId}/timeline`)
      .pipe(unwrapValidation(null));
  }

  /**
   * Empréstimos com status Cotado da empresa do deal, para a aba Cotação (card 3611). Mesma rota para o
   * time interno e para o parceiro: o servidor confere que o deal é visível para quem pede e usa a empresa
   * do próprio deal. Deal de outro parceiro volta como erro ("Deal não encontrado").
   */
  getDealQuotedLoans(dealId: string): Observable<PartnerDealQuotedLoanResponse[]> {
    return this.apiService
      .get<PartnerDealQuotedLoanResponse[]>(`${this.BASE_URL}/deals/${dealId}/quoted-loans`)
      .pipe(map(loans => loans ?? []));
  }

  /**
   * Jornada nos bancos de um deal (card 3707): as negociações do pipe de crédito ligadas a ele, já traduzidas
   * para a etapa do parceiro (`cards`). `unlocked` = há pelo menos uma, em qualquer etapa do deal; é o que mostra
   * a aba Bancos e o cartão da Visão geral. Deal que quem pede não vê volta como erro ("Deal não encontrado").
   */
  getDealBankJourney(dealId: string): Observable<PartnerBankJourneyResponse> {
    return this.apiService
      .get<PartnerBankJourneyResponse>(`${this.BASE_URL}/deals/${dealId}/bank-journey`)
      .pipe(map(journey => journey ?? { unlocked: false, deals: [], cards: [] }));
  }

  /**
   * Jornada nos bancos de **todos** os deals que quem pede vê (card 3765, visão "Jornada nos bancos" da tela Meus
   * deals): mesma régua do quadro de deals (master todos do parceiro, comum os que registrou, interno os do tenant).
   * `deals` lista os deals com banco, para o filtro.
   */
  getBankJourney(): Observable<PartnerBankJourneyResponse> {
    return this.apiService
      .get<PartnerBankJourneyResponse>(`${this.BASE_URL}/bank-journey`)
      .pipe(map(journey => journey ?? { unlocked: false, deals: [], cards: [] }));
  }

  /** Comentários de um banco da jornada, em ordem cronológica (card 3612). */
  getBankJourneyComments(pipelineDealId: string): Observable<PartnerBankJourneyCommentResponse[]> {
    return this.apiService
      .get<
        PartnerBankJourneyCommentResponse[]
      >(`${this.BASE_URL}/bank-journey/${pipelineDealId}/comments`)
      .pipe(map(comments => comments ?? []));
  }

  /**
   * Comenta num banco da jornada (card 3612). Só em Envio de documentação e Análise do crédito; fora delas o
   * backend recusa com a mensagem que diz em que etapa o banco está, e a recusa chega como erro.
   */
  addBankJourneyComment(
    pipelineDealId: string,
    text: string
  ): Observable<PartnerBankJourneyCommentResponse | null> {
    return this.apiService
      .post<
        ValidationResultResponse<PartnerBankJourneyCommentResponse>
      >(`${this.BASE_URL}/bank-journey/${pipelineDealId}/comments`, { text })
      .pipe(unwrapValidation(null));
  }

  /**
   * Estado da aba Briefing do deal (card 3878): prontidão, catálogo de seções, versões e a versão atual. Só o time
   * interno: parceiro e cliente recebem 403, que chega como erro.
   */
  getDealBriefing(dealId: string): Observable<PartnerDealBriefingSummaryResponse | null> {
    return this.apiService.get<PartnerDealBriefingSummaryResponse>(
      `${this.BASE_URL}/deals/${dealId}/briefing`
    );
  }

  /**
   * Pede uma geração do briefing com as seções escolhidas (card 3878). Duplo clique ou duas pessoas devolvem a versão
   * que já está na fila ou gerando; prontidão bloqueada volta como erro com a mensagem do backend (422).
   */
  generateDealBriefing(
    dealId: string,
    sections: string[]
  ): Observable<PartnerDealBriefingVersionIdResponse | null> {
    const body: GeneratePartnerDealBriefingRequest = { sections };
    return this.apiService.post<PartnerDealBriefingVersionIdResponse>(
      `${this.BASE_URL}/deals/${dealId}/briefing/generate`,
      body
    );
  }

  /** Marca uma versão pronta do briefing como a atual do deal (card 3878). */
  setCurrentDealBriefing(dealId: string, versionId: string): Observable<boolean | null> {
    return this.apiService.post<boolean>(
      `${this.BASE_URL}/deals/${dealId}/briefing/versions/${versionId}/current`,
      {}
    );
  }

  /**
   * PDF de uma versão pronta do briefing (card 3878). Vai pelo `downloadWithProgress` porque ele lê o envelope de
   * erro que chega como `Blob` (422 "O PDF desta versão não foi encontrado...") e devolve a mensagem do backend.
   */
  getDealBriefingPdf(dealId: string, versionId: string): Observable<Blob | null> {
    return this.apiService
      .downloadWithProgress(
        'get',
        `${this.BASE_URL}/deals/${dealId}/briefing/versions/${versionId}/pdf`
      )
      .pipe(
        filter(event => event.type === HttpEventType.Response),
        map(event => (event as HttpResponse<Blob>).body)
      );
  }

  /**
   * HTML da visualização de uma versão do briefing (card 3878, Q3): o conteúdo final da versão pronta ou o último
   * rascunho da que está gerando, para pôr num `iframe` com `srcdoc` e `sandbox="allow-scripts"`. A rota exige o
   * Bearer, por isso não dá para usar a URL no `src`. Vai pelo `downloadWithProgress` porque o erro (404 sem rascunho,
   * 502 do Doc-Generate) chega como `Blob` e ele devolve a mensagem e o campo do backend (`preview`, `docGenerate`).
   * Cada carga pesa de 1 a 1,5 MB (fontes e Paged.js embutidos).
   */
  getDealBriefingHtml(dealId: string, versionId: string): Observable<string | null> {
    return this.apiService
      .downloadWithProgress(
        'get',
        `${this.BASE_URL}/deals/${dealId}/briefing/versions/${versionId}/html`
      )
      .pipe(
        filter(event => event.type === HttpEventType.Response),
        switchMap(event => {
          const body = (event as HttpResponse<Blob>).body;
          return body ? from(body.text()) : of(null);
        })
      );
  }

  /**
   * Investigação da empresa do deal (card 3911): a mais recente do CNPJ no tenant, com o histórico; `null` quando o
   * CNPJ nunca foi investigado. Mesma régua de acesso do briefing.
   */
  getDealInvestigation(dealId: string): Observable<PartnerDealInvestigationResponse | null> {
    return this.apiService.get<PartnerDealInvestigationResponse>(
      `${this.BASE_URL}/deals/${dealId}/briefing/investigation`
    );
  }

  /**
   * "Refazer investigação" (card 3911): cria uma nova mesmo dentro da validade. Já havendo uma na fila ou rodando, o
   * backend recusa com a mensagem pronta (400). Lista vazia usa as subseções ligadas na configuração.
   */
  rerunDealInvestigation(
    dealId: string,
    subsections: string[]
  ): Observable<ValidationResultResponse<PartnerDealInvestigationResponse> | null> {
    return this.apiService.post<ValidationResultResponse<PartnerDealInvestigationResponse>>(
      `${this.BASE_URL}/deals/${dealId}/briefing/investigation/rerun`,
      { subsections }
    );
  }

  /**
   * "Completar investigação" (card 3911): versão nova só de render, quando a investigação da versão terminou depois do
   * prazo do PDF.
   */
  completeDealBriefingInvestigation(
    dealId: string,
    versionId: string
  ): Observable<PartnerDealBriefingVersionIdResponse | null> {
    return this.apiService.post<PartnerDealBriefingVersionIdResponse>(
      `${this.BASE_URL}/deals/${dealId}/briefing/versions/${versionId}/complete-investigation`,
      {}
    );
  }

  /**
   * Contexto do chat do briefing (card 3878): base, conteúdo da versão atual e link curto do PDF. Quem consome é o Cog,
   * com o token de quem conversa; a aba manda só o `dealId` ao chat e não precisa desta chamada para abrir o painel.
   */
  getDealBriefingChatContext(
    dealId: string
  ): Observable<PartnerDealBriefingChatContextResponse | null> {
    return this.apiService.get<PartnerDealBriefingChatContextResponse>(
      `${this.BASE_URL}/deals/${dealId}/briefing/chat-context`
    );
  }

  /**
   * Gates de elegibilidade do deal (card 3281, T05), calculados no servidor com os números extraídos
   * da DFP da empresa e os limites da Parametrização. Só o time interno: para o parceiro, `null`.
   */
  getDealGates(dealId: string): Observable<PartnerDealGatesResponse | null> {
    return this.apiService
      .get<
        ValidationResultResponse<PartnerDealGatesResponse>
      >(`${this.BASE_URL}/deals/${dealId}/gates`)
      .pipe(unwrapValidation(null));
  }

  /**
   * Só o andamento do processamento dos documentos da empresa do deal (card 3300), sem recalcular a
   * DFP. É a consulta leve que o painel de gates repete enquanto há documento processando.
   */
  getDealGatesProcessing(dealId: string): Observable<PartnerDealGatesProcessingResponse | null> {
    return this.apiService
      .get<
        ValidationResultResponse<PartnerDealGatesProcessingResponse>
      >(`${this.BASE_URL}/deals/${dealId}/gates/processing`)
      .pipe(unwrapValidation(null));
  }

  /**
   * O NDA + carta de autorização de SCR do cliente deste deal.
   *
   * Devolve `null` quando o termo ainda não foi solicitado — que é situação normal, e não erro:
   * a tela mostra o convite para solicitar.
   *
   * ⚠️ O termo é **por cliente**, não por deal: dois deals da mesma empresa veem o mesmo termo.
   */
  getDealTerm(dealId: string): Observable<CommercialClientTermResponse | null> {
    return this.apiService
      .get<
        ValidationResultResponse<CommercialClientTermResponse>
      >(`${this.BASE_URL}/deals/${dealId}/term`)
      .pipe(unwrapValidation(null));
  }

  /**
   * Sócios do cliente na Receita, para sugerir quem deve assinar.
   *
   * ⚠️ Vêm com **nome e qualificação apenas** — a Receita não informa e-mail nem CPF, e o
   * provedor de assinatura exige e-mail. A sugestão diz *quem*; o endereço é digitado na modal.
   *
   * Lista vazia não é erro: CNPJ sem sócios, empresa individual ou consulta indisponível chegam
   * todos assim, e a inclusão manual segue disponível.
   */
  getDealTermSuggestedSignatories(
    dealId: string
  ): Observable<CommercialClientTermSuggestedSignatoryResponse[] | null> {
    return this.apiService
      .get<
        ValidationResultResponse<CommercialClientTermSuggestedSignatoryResponse[]>
      >(`${this.BASE_URL}/deals/${dealId}/term/suggested-signatories`)
      .pipe(unwrapValidation([]));
  }

  /**
   * Quem assina pela Legatus neste termo, vindo da Parametrização.
   *
   * Só leitura — o parceiro não escolhe a contraparte. Lista vazia significa que ninguém foi
   * configurado, e o envio seria recusado: a tela avisa antes de a pessoa escolher assinantes.
   */
  getDealTermLegatusSignatories(dealId: string): Observable<LegatusTermSignatoryResponse[] | null> {
    return this.apiService
      .get<
        ValidationResultResponse<LegatusTermSignatoryResponse[]>
      >(`${this.BASE_URL}/deals/${dealId}/term/legatus-signatories`)
      .pipe(unwrapValidation([]));
  }

  /**
   * Como o signatário do cliente confirma a identidade (card 3073). A modal usa `requiresPhone`
   * para tornar o celular obrigatório; `null` quando a consulta falha — a tela segue com o
   * padrão (e-mail), e o backend recusa o envio se a configuração exigir celular.
   */
  getDealTermSignerVerification(dealId: string): Observable<TermSignerVerificationResponse | null> {
    return this.apiService
      .get<
        ValidationResultResponse<TermSignerVerificationResponse>
      >(`${this.BASE_URL}/deals/${dealId}/term/signer-verification`)
      .pipe(unwrapValidation(null));
  }

  /**
   * Compõe o documento que **será** enviado, com os assinantes escolhidos.
   *
   * ⚠️ É `POST` mas **não grava nada** — nem termo, nem blob. O verbo é por causa do corpo: a
   * escolha de assinantes ainda não existe em lugar nenhum para ser referenciada por id.
   *
   * Passa pelas mesmas regras do envio, então uma recusa aqui é a mesma que apareceria depois
   * do "confirmar" — e é justamente isso que o passo de conferência existe para antecipar.
   */
  previewDealTerm(
    dealId: string,
    signatories: AddCommercialClientTermSignatoryRequest[]
  ): Observable<CommercialClientTermPreviewResponse | null> {
    return this.apiService
      .post<
        ValidationResultResponse<CommercialClientTermPreviewResponse>
      >(`${this.BASE_URL}/deals/${dealId}/term/preview`, signatories)
      .pipe(unwrapValidation(null));
  }

  /**
   * Link temporário para **ver** o documento do termo.
   *
   * Devolve a via assinada quando ela existe, e a minuta enquanto não existe — `signed` diz qual
   * dos dois. Termo sem arquivo arquivado volta com erro, e não com link vazio: link vazio faria
   * a tela abrir uma aba em branco.
   */
  getDealTermDocument(dealId: string): Observable<CommercialClientTermDocumentResponse | null> {
    return this.apiService
      .get<
        ValidationResultResponse<CommercialClientTermDocumentResponse>
      >(`${this.BASE_URL}/deals/${dealId}/term/document`)
      .pipe(unwrapValidation(null));
  }

  /**
   * Envia o termo para assinatura com os assinantes do cliente escolhidos.
   *
   * Os assinantes da **Legatus** não vão no corpo: o servidor os resolve pela Parametrização.
   */
  requestDealTermSignature(
    dealId: string,
    signatories: AddCommercialClientTermSignatoryRequest[]
  ): Observable<CommercialClientTermResponse | null> {
    return this.apiService
      .post<
        ValidationResultResponse<CommercialClientTermResponse>
      >(`${this.BASE_URL}/deals/${dealId}/term`, signatories)
      .pipe(unwrapValidation(null));
  }

  /**
   * Acrescenta um assinante do cliente a um termo já aberto.
   *
   * ⚠️ **Isto cancela o documento no provedor e abre outro, em branco** — as assinaturas já
   * colhidas são perdidas e todos assinam de novo. É como o Clicksign funciona; a tela precisa
   * avisar antes de confirmar.
   */
  addDealTermSignatory(
    dealId: string,
    signatory: AddCommercialClientTermSignatoryRequest
  ): Observable<CommercialClientTermResponse | null> {
    return this.apiService
      .post<
        ValidationResultResponse<CommercialClientTermResponse>
      >(`${this.BASE_URL}/deals/${dealId}/term/signatories`, signatory)
      .pipe(unwrapValidation(null));
  }

  /**
   * Remove um assinante do cliente.
   *
   * ⚠️ Mesmo efeito do acréscimo: o documento é **cancelado e recriado**, e quem já tinha
   * assinado assina de novo.
   */
  removeDealTermSignatory(
    dealId: string,
    signatoryId: string
  ): Observable<CommercialClientTermResponse | null> {
    return this.apiService
      .delete<
        ValidationResultResponse<CommercialClientTermResponse>
      >(`${this.BASE_URL}/deals/${dealId}/term/signatories/${signatoryId}`)
      .pipe(unwrapValidation(null));
  }

  /** Reenvia por e-mail a cobrança de assinatura a quem ainda não assinou. */
  resendDealTermSignature(dealId: string, signatoryId: string): Observable<boolean | null> {
    return this.apiService
      .post<
        ValidationResultResponse<boolean>
      >(`${this.BASE_URL}/deals/${dealId}/term/signatories/${signatoryId}/resend`, {})
      .pipe(unwrapValidation(false));
  }

  /**
   * Reenvia o convite a **todos** os signatários pendentes de uma vez.
   *
   * Convive com o reenvio individual: cobrar cinco pessoas em cinco cliques é trabalho que a tela
   * deve poupar, mas cobrar *uma* continua necessário quando só falta ela.
   *
   * ⚠️ Devolve **quantos** foram reenviados, e não um booleano — um destinatário que falha não
   * interrompe os demais, e "3 de 5" é o que a tela precisa dizer.
   */
  resendAllDealTermSignatures(dealId: string): Observable<number | null> {
    return this.apiService
      .post<
        ValidationResultResponse<number>
      >(`${this.BASE_URL}/deals/${dealId}/term/resend-all`, {})
      .pipe(unwrapValidation(0));
  }

  /**
   * Pré-aprova o deal — ação da Legatus, indisponível para o parceiro.
   *
   * Move `Registro` → `Consentimento`, ou **direto para `Documentacao`** quando o cliente já
   * assinou o NDA em outro deal (o termo é um por cliente). Avisa o parceiro por e-mail.
   */
  preApproveDeal(dealId: string): Observable<PartnerDealResponse | null> {
    return this.apiService
      .post<
        ValidationResultResponse<PartnerDealResponse>
      >(`${this.BASE_URL}/deals/${dealId}/pre-approve`, {})
      .pipe(unwrapValidation(null));
  }

  /**
   * Rejeita o deal, com motivo, e avisa o parceiro.
   *
   * ⚠️ A **etapa não muda**: rejeição é desfecho, não lugar no funil, e a etapa fica congelada
   * para o deal poder ser reaberto de onde parou. Use `rejectedAt` para o chip de situação, nunca
   * o `stage`.
   */
  rejectDeal(
    dealId: string,
    payload: RejectPartnerDealRequest
  ): Observable<PartnerDealResponse | null> {
    return this.apiService
      .post<
        ValidationResultResponse<PartnerDealResponse>
      >(`${this.BASE_URL}/deals/${dealId}/reject`, payload)
      .pipe(unwrapValidation(null));
  }

  /**
   * Apaga o deal (exclusão lógica) pelo menu do card em Meus deals (card 3324).
   *
   * O backend decide quem pode e em que etapa; recusa volta como `ErrorResponse` com a mensagem
   * que diz o porquê. Os documentos do deal continuam no Data Room da empresa.
   */
  deleteDeal(dealId: string): Observable<boolean> {
    return this.apiService
      .delete<ValidationResultResponse<boolean>>(`${this.BASE_URL}/deals/${dealId}`)
      .pipe(unwrapValidation(false));
  }

  /** Reabre um deal rejeitado, com justificativa. A rejeição continua na linha do tempo. */
  revertDealRejection(
    dealId: string,
    payload: RevertPartnerDealRejectionRequest
  ): Observable<PartnerDealResponse | null> {
    return this.apiService
      .post<
        ValidationResultResponse<PartnerDealResponse>
      >(`${this.BASE_URL}/deals/${dealId}/revert-rejection`, payload)
      .pipe(unwrapValidation(null));
  }

  /**
   * Sobe um documento no deal. Responde antes de o documento ser classificado — a
   * identificação pelo agente acontece numa fila, e o andamento vem por
   * {@link getDealDocuments}.
   */
  uploadDealDocument(dealId: string, file: File): Observable<PartnerDealDocumentResponse | null> {
    const form = new FormData();
    form.append('file', file, file.name);

    return this.apiService.post<PartnerDealDocumentResponse>(
      `${this.BASE_URL}/deals/${dealId}/documents`,
      form
    );
  }

  /**
   * Move o deal para outra etapa — o que o arrasto do card persiste.
   *
   * A tela move o card na hora (atualização otimista) e devolve para a coluna de origem
   * se isto recusar.
   */
  /**
   * Move o deal de etapa e, com `order`, põe o card nessa posição da coluna (a partir de 1,
   * card 3307). Na mesma etapa com `order`, só reordena. `reason` é o motivo da volta de etapa
   * (card 3579): vai para a linha do tempo e para o e-mail ao parceiro.
   */
  updateDealStage(
    dealId: string,
    stage: PartnerDealStage,
    order?: number,
    reason?: string | null
  ): Observable<PartnerDealResponse | null> {
    return this.apiService.patch<PartnerDealResponse>(`${this.BASE_URL}/deals/${dealId}/stage`, {
      stage,
      order: order ?? null,
      reason: reason ?? null,
    });
  }

  /**
   * Corrige o valor pretendido e a moeda de um deal.
   *
   * Só o usuário interno e o **master** do parceiro dono conseguem — o backend recusa o
   * parceiro comum, inclusive nos deals que ele mesmo registrou. Use
   * `PartnerDealResponse.canEditAmount` para decidir se a ação aparece; não reimplemente a
   * régua na tela.
   */
  updateDealAmount(
    dealId: string,
    payload: UpdatePartnerDealAmountRequest
  ): Observable<PartnerDealResponse | null> {
    return this.apiService.patch<PartnerDealResponse>(
      `${this.BASE_URL}/deals/${dealId}/amount`,
      payload
    );
  }

  /** Documentos do deal com o andamento de cada um (consultado em polling pela tela). */
  getDealDocuments(dealId: string): Observable<PartnerDealDocumentResponse[] | null> {
    return this.apiService.get<PartnerDealDocumentResponse[]>(
      `${this.BASE_URL}/deals/${dealId}/documents`
    );
  }

  // --- Gerenciamento › Parceiros (card 2916) — só o time interno alcança ---

  /**
   * Página da listagem de parceiros, com busca por nome e e-mail.
   *
   * Traz ativos e inativos: a tela precisa enxergar o inativo justamente para reativá-lo.
   */
  getPartners(
    page: number,
    pageSize: number,
    filter?: string
  ): Observable<PartnerPageResponse | null> {
    return this.apiService.get<PartnerPageResponse>(this.BASE_URL, undefined, {
      Page: page,
      PageSize: pageSize,
      // Sem `Filter` vazio na query: o backend trata null como "sem busca", e string vazia
      // entraria no `Contains` como um filtro que casa com tudo — mesmo resultado, só que
      // passando pelo caminho de busca à toa.
      ...(filter ? { Filter: filter } : {}),
    });
  }

  /** Um parceiro, para a tela de edição. */
  getPartnerById(id: string): Observable<PartnerResponse | null> {
    return this.apiService.get<PartnerResponse>(`${this.BASE_URL}/${id}`);
  }

  /**
   * Cadastra o parceiro e o acesso dele de uma vez.
   *
   * O parceiro recebe por e-mail um **link** para a tela de criar senha, onde aceita os
   * termos — não o código de 6 dígitos, que é da landing.
   */
  createPartner(payload: CreatePartnerRequest): Observable<PartnerResponse | null> {
    return this.apiService.post<PartnerResponse>(this.BASE_URL, payload);
  }

  /** Edita nome, telefone e — enquanto o convite não foi aceito — o e-mail. */
  updatePartner(id: string, payload: UpdatePartnerRequest): Observable<PartnerResponse | null> {
    return this.apiService.put<PartnerResponse>(`${this.BASE_URL}/${id}`, payload);
  }

  /**
   * Ativa ou inativa o parceiro.
   *
   * Inativar trava o login dele e o registro de deal novo — e nada além disso: os deals já
   * registrados continuam no board.
   */
  setPartnerActive(id: string, active: boolean): Observable<PartnerResponse | null> {
    return this.apiService.patch<PartnerResponse>(`${this.BASE_URL}/${id}/active`, { active });
  }

  /** Empresas no escopo de um parceiro, com a origem de cada uma. */
  getPartnerCompanies(partnerId: string): Observable<PartnerCompanyResponse[] | null> {
    return this.apiService.get<PartnerCompanyResponse[]>(`${this.BASE_URL}/${partnerId}/companies`);
  }

  /**
   * Empresas que ainda podem ser vinculadas a este parceiro.
   *
   * Vem do servidor, e não de um filtro sobre a listagem geral de empresas: o critério é "não
   * estar com nenhum parceiro", e a tela só conhece o escopo do parceiro aberto. De quebra,
   * isso evita o `PageSize: 1000` do `getAllCompanys`, que trunca em silêncio.
   */
  getAvailableCompanies(partnerId: string): Observable<PartnerCompanyOptionResponse[] | null> {
    return this.apiService.get<PartnerCompanyOptionResponse[]>(
      `${this.BASE_URL}/${partnerId}/companies/available`
    );
  }

  /**
   * Vincula **uma** empresa ao parceiro, registrando junto o **Deal manual**.
   *
   * Uma por vez desde 18/09/2026: o vínculo passou a carregar um valor pretendido próprio, e
   * não existe um valor que sirva para várias empresas de uma vez. O desvínculo (abaixo)
   * continua aceitando lista, porque ali não há nada por empresa a informar.
   */
  linkPartnerCompany(
    partnerId: string,
    payload: LinkPartnerCompanyRequest
  ): Observable<void | null> {
    return this.apiService.post<void>(`${this.BASE_URL}/${partnerId}/companies`, payload);
  }

  /**
   * Desvincula empresas do parceiro.
   *
   * `POST` numa sub-rota, e não `DELETE`: a operação manda uma lista de ids no corpo, e
   * corpo em `DELETE` não é encaminhado pelo `VaultApiService` (o wrapper só aceita query
   * params) além de ser descartado por parte dos proxies.
   */
  removePartnerCompanies(partnerId: string, companyIds: string[]): Observable<void | null> {
    return this.apiService.post<void>(`${this.BASE_URL}/${partnerId}/companies/unlink`, {
      companyIds,
    });
  }

  // --- Comercial › Usuários: a equipe do PRÓPRIO parceiro, administrada pelo master ---
  //
  // ⚠️ Nenhuma destas rotas leva id de parceiro, e isso é do desenho do endpoint, não
  // omissão: o backend resolve o parceiro pelo usuário logado. Se o id viajasse aqui, a
  // autorização passaria a depender de o servidor lembrar de comparar o valor recebido com
  // o do token — e é essa comparação que se esquece num endpoint novo. Sem o parâmetro, não
  // há o que comparar.

  /**
   * A equipe do parceiro do usuário logado.
   *
   * Traz os master e os inativados junto — o master precisa ver o quadro completo da equipe
   * dele. Quem omite as ações nessas linhas é a tela.
   */
  getMyPartnerUsers(): Observable<PartnerUserResponse[] | null> {
    return this.apiService.get<PartnerUserResponse[]>(`${this.BASE_URL}/my-users`);
  }

  /**
   * Cadastra um usuário na equipe, com o perfil escolhido no formulário (`asMaster`), e manda
   * por e-mail um link para criar a senha.
   *
   * ⚠️ `asMaster: true` não tem volta pelo parceiro — ver {@link promoteMyPartnerUserToMaster}.
   */
  createMyPartnerUser(payload: CreatePartnerUserRequest): Observable<PartnerUserResponse | null> {
    return this.apiService.post<PartnerUserResponse>(`${this.BASE_URL}/my-users`, payload);
  }

  /** Edita nome, sobrenome e telefone — e o e-mail, enquanto o usuário não criou senha. */
  updateMyPartnerUser(
    userId: string,
    payload: UpdatePartnerUserRequest
  ): Observable<PartnerUserResponse | null> {
    return this.apiService.put<PartnerUserResponse>(`${this.BASE_URL}/my-users/${userId}`, payload);
  }

  /**
   * Trava ou destrava o login de um usuário da equipe.
   *
   * ⚠️ Não expira a sessão dele: a checagem está no login, e um token já emitido vale por
   * até uma hora. A confirmação da tela precisa dizer isso.
   */
  setMyPartnerUserActive(userId: string, active: boolean): Observable<void | null> {
    return this.apiService.patch<void>(`${this.BASE_URL}/my-users/${userId}/active`, {
      active,
    });
  }

  /**
   * Reenvia o e-mail com o link de criar senha.
   *
   * `POST` e não `GET` porque manda e-mail — `GET` é cacheável e o navegador pode
   * pré-carregá-lo, disparando o convite sem ninguém clicar.
   */
  resendMyPartnerUserInvite(userId: string): Observable<void | null> {
    return this.apiService.post<void>(`${this.BASE_URL}/my-users/${userId}/invite`, {});
  }

  /**
   * Promove um usuário comum da equipe a **master** do parceiro.
   *
   * ⚠️ **Não existe o inverso.** Usuário master fica fora do alcance das ações do master, e
   * rebaixar é da Legatus, pela tela de grupos de permissões. É por isso que a rota é
   * `POST .../promote-master` e não um `PATCH .../master` com booleano: um toggle prometeria
   * um caminho de volta que a API não oferece.
   */
  promoteMyPartnerUserToMaster(userId: string): Observable<void | null> {
    return this.apiService.post<void>(`${this.BASE_URL}/my-users/${userId}/promote-master`, {});
  }
}
